import type { IState } from '@montugroup/circuit-shared';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { Box, Button, Stack, TextField } from '@montugroup/design-system';
import { MenuItem } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

type ManualShippingAddressProps = {
  onSubmit: (data: Address) => void;
  onCancel: () => void;
  states: IState[];
  isLoading?: boolean;
};

export default function ManualShippingAddress({ onSubmit, onCancel, states, isLoading }: ManualShippingAddressProps) {
  const {
    control,
    formState: { errors, isSubmitting, isDirty },
    getValues,
    trigger,
  } = useFormContext<Address>();

  const requiredFields = ['addressLine1', 'suburb', 'postcode', 'state'] as const;
  const values = getValues(requiredFields);

  const handleUpdateClick = async () => {
    const valid = await trigger(['addressLine1', 'suburb', 'state', 'postcode']);
    if (!valid) {
      return;
    }
    onSubmit(getValues());
  };
  const hasRequiredEmpty = values.some((v) => !v);
  return (
    <Box sx={{ mt: 4 }}>
      <Stack rowGap={4} paddingTop={4}>
        <Controller
          name="addressLine1"
          control={control}
          rules={{ required: 'Address is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Address"
              fullWidth
              error={!!errors.addressLine1}
              helperText={errors.addressLine1?.message}
            />
          )}
        />

        <Stack direction="row" width="100%" columnGap={4}>
          <Controller
            name="addressLine2"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address Line 2"
                fullWidth
                error={!!errors.addressLine2}
                helperText={errors.addressLine2?.message}
              />
            )}
          />

          <Controller
            name="suburb"
            control={control}
            rules={{
              required: 'Suburb is required',
              pattern: {
                value: /^[A-Za-z\s'-]+$/,
                message: 'Please add valid suburb',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Suburb"
                fullWidth
                error={!!errors.suburb}
                helperText={errors.suburb?.message}
              />
            )}
          />
        </Stack>

        <Stack direction="row" width="100%" columnGap={4}>
          <Controller
            name="state"
            control={control}
            rules={{ required: 'State is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="State"
                fullWidth
                error={!!errors.state}
                helperText={errors.state?.message}
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {states.map((s) => (
                  <MenuItem key={s.id} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="postcode"
            control={control}
            rules={{ required: 'Postcode is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Postcode"
                type="number"
                fullWidth
                error={!!errors.postcode}
                helperText={errors.postcode?.message}
              />
            )}
          />
        </Stack>

        <Stack direction={'row'} width={'100%'} columnGap={4}>
          <Button type="button" fullWidth variant="outlined" onClick={onCancel} disabled={hasRequiredEmpty}>
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            disabled={isSubmitting || !isDirty || isLoading}
            variant="contained"
            onClick={handleUpdateClick}
          >
            Update
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
